// hooks/jobOffers/useJobOffers.ts
import { useState, useEffect, useCallback } from "react";
import { JobOffer, JobOfferToBackend } from "../../../types/features/JobOffer";
import { jobOfferService } from "../../../services/jobOfferService";
import useToken from "../auth/useToken";

export default function useJobOffers() {
  const { token } = useToken();
  const [jobOffers, setJobOffers] = useState<JobOffer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchJobOffers = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await jobOfferService.getAll(page, perPage);
      setJobOffers(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des offres d'emploi");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchJobOfferById = useCallback(async (id: string): Promise<JobOffer | null> => {
    setLoading(true);
    setError(null);
    try {
      const offer = await jobOfferService.getById(id);
      if (!offer) {
        setError("Offre non trouvée");
      }
      return offer;
    } catch (err) {
      setError("Erreur lors du chargement de l'offre");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchJobOfferBySlug = useCallback(async (slug: string): Promise<JobOffer | null> => {
    setLoading(true);
    setError(null);
    try {
      const offer = await jobOfferService.getBySlug(slug);
      if (!offer) {
        setError("Offre non trouvée");
      }
      return offer;
    } catch (err) {
      setError("Erreur lors du chargement de l'offre");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔒 PROTÉGÉ - Token requis
  const createJobOffer = useCallback(async (data: JobOfferToBackend): Promise<JobOffer | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newOffer = await jobOfferService.create(token, data);
      setJobOffers((prev) => [newOffer, ...prev]);
      return newOffer;
    } catch (err) {
      setError("Erreur lors de la création de l'offre");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const updateJobOffer = useCallback(async (id: string, data: Partial<JobOfferToBackend>): Promise<JobOffer | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const updatedOffer = await jobOfferService.update(token, id, data);
      setJobOffers((prev) => prev.map((o) => (o.id === id ? updatedOffer : o)));
      return updatedOffer;
    } catch (err) {
      setError("Erreur lors de la mise à jour de l'offre");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteJobOffer = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await jobOfferService.delete(token, id);
      setJobOffers((prev) => prev.filter((o) => o.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de l'offre");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteManyJobOffers = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await jobOfferService.deleteMany(token, ids);
      setJobOffers((prev) => prev.filter((o) => !ids.includes(o.id)));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const toggleVisibility = useCallback(async (id: string, currentStatus: boolean): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    try {
      const updated = await jobOfferService.update(token, id, { is_visible: !currentStatus });
      setJobOffers((prev) => prev.map((o) => (o.id === id ? updated : o)));
      return true;
    } catch (err) {
      setError("Erreur lors du changement de visibilité");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchJobOffers();
  }, [fetchJobOffers]);

  return {
    jobOffers,
    loading,
    error,
    pagination,
    fetchJobOffers,
    fetchJobOfferById,
    fetchJobOfferBySlug,
    createJobOffer,
    updateJobOffer,
    deleteJobOffer,
    deleteManyJobOffers,
    toggleVisibility,
  };
}